import { useRef, useState } from 'react';
import { ImagePlus, Trash2, Link2, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function ImageUpload({ value, onChange, label = 'Image', testId = 'image-upload' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showUrl, setShowUrl] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error("Choisissez une image (JPG, PNG, WEBP)"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image trop lourde (5 Mo maximum)"); return; }
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true); setProgress(0);
    try {
      const r = await api.post('/admin/upload', fd, { onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)) });
      onChange(r.data.url);
      toast.success("Image ajoutée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'envoi de l'image");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div data-testid={testId}>
      <Label className="text-xs font-medium text-slate-500">{label}</Label>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid={`${testId}-input`} />
      <div className="mt-1 flex items-start gap-3">
        <div
          className={`relative h-24 w-32 shrink-0 rounded-xl border-2 border-dashed overflow-hidden flex items-center justify-center cursor-pointer transition-colors ${value ? 'border-slate-200' : 'border-slate-300 hover:border-violet-400 hover:bg-violet-50/40'}`}
          onClick={() => !uploading && inputRef.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); }}
          data-testid={`${testId}-dropzone`}
        >
          {value ? <img src={value} alt="" className="h-full w-full object-cover" data-testid={`${testId}-preview`} /> : <ImagePlus className="h-6 w-6 text-slate-400" />}
          {uploading && (
            <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center text-xs text-violet-700 font-medium">
              <Loader2 className="h-5 w-5 animate-spin mb-1" />{progress}%
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid={`${testId}-choose-btn`}>
              {value ? <><RefreshCw className="h-3 w-3 mr-1" /> Remplacer</> : <><ImagePlus className="h-3 w-3 mr-1" /> Choisir une image</>}
            </Button>
            {value && (
              <Button type="button" size="sm" variant="ghost" className="rounded-full text-xs text-rose-600 hover:bg-rose-50" onClick={() => onChange('')} data-testid={`${testId}-remove-btn`}>
                <Trash2 className="h-3 w-3 mr-1" /> Retirer
              </Button>
            )}
            <Button type="button" size="sm" variant="ghost" className="rounded-full text-xs text-slate-500" onClick={() => setShowUrl(s => !s)} data-testid={`${testId}-url-toggle`}>
              <Link2 className="h-3 w-3 mr-1" /> Lien
            </Button>
          </div>
          <p className="text-[11px] text-slate-400">JPG, PNG ou WEBP · 5 Mo max · redimensionnée et allégée automatiquement · glisser-déposer possible</p>
          {showUrl && <Input value={value || ''} onChange={e => onChange(e.target.value)} placeholder="https://..." className="rounded-lg h-8 text-xs" data-testid={`${testId}-url-input`} />}
        </div>
      </div>
    </div>
  );
}
